import { today } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import { useMutation } from "@tanstack/react-query";
import z from "zod";

import { useTRPC } from "@/integrations/trpc/react";
import { getDefaultTimeZone } from "@/lib/dates";
import { calendarDateSchema } from "@/lib/schemas";

import { useAppForm } from "../../base/form";
import { Radio } from "../../base/radio-group";

const formSchema = z.object({
  startDate: calendarDateSchema(),
  endDate: calendarDateSchema(),
  visibility: z.enum(["publish", "unpublish"]),
});

export function PublishScheduleForm({ onSuccess }: { onSuccess?: () => void }) {
  const trpc = useTRPC();
  const { mutate } = useMutation({
    ...trpc.tournaments.publishSchedule.mutationOptions(),
    onSuccess,
  });

  const form = useAppForm({
    defaultValues: {
      startDate: today(getDefaultTimeZone()).set({ day: 1 }),
      endDate: today(getDefaultTimeZone()).set({ month: 12, day: 31 }),
      visibility: "publish" as "publish" | "unpublish",
    },
    validators: {
      onMount: formSchema,
      onChange: formSchema,
    },
    onSubmit: ({ value: { startDate, endDate, visibility } }) => {
      mutate({
        startDate: startDate.toString(),
        endDate: endDate.toString(),
        visible: visibility === "publish",
      });
    },
  });

  const dateFormatter = useDateFormatter({
    dateStyle: "short",
    timeZone: getDefaultTimeZone(),
  });

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();

        form.handleSubmit();
      }}
    >
      <form.AppField
        name="startDate"
        children={(field) => (
          <field.DatePicker
            label="Start Date"
            className="col-span-2"
            field={field}
          />
        )}
      />

      <form.AppField
        name="endDate"
        children={(field) => (
          <field.DatePicker
            label="End Date"
            className="col-span-2"
            field={field}
          />
        )}
      />

      <form.AppField
        name="visibility"
        children={(field) => (
          <field.RadioGroup
            label="Visibility"
            field={field}
            mode="string"
            orientation="horizontal"
          >
            <Radio value="publish">Publish</Radio>
            <Radio value="unpublish">Unpublish</Radio>
          </field.RadioGroup>
        )}
      />

      <form.AppForm>
        <form.Footer>
          <form.ConfirmSubmitButton
            requireChange={false}
            description={
              <div className="space-y flex flex-col">
                <div className="flex flex-row space-x-2">
                  <span className="font-semibold">Start date:</span>
                  <span>
                    {dateFormatter.format(
                      form.state.values.startDate.toDate(getDefaultTimeZone()),
                    )}
                  </span>
                </div>
                <div className="flex flex-row space-x-2">
                  <span className="font-semibold">End date:</span>
                  <span>
                    {dateFormatter.format(
                      form.state.values.endDate.toDate(getDefaultTimeZone()),
                    )}
                  </span>
                </div>
                <div className="flex flex-row space-x-2">
                  <span className="font-semibold">Action:</span>
                  <span>
                    {form.state.values.visibility === "publish"
                      ? "Publish"
                      : "Unpublish"}
                  </span>
                </div>
              </div>
            }
          >
            Submit
          </form.ConfirmSubmitButton>
        </form.Footer>
      </form.AppForm>
    </form>
  );
}
